import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  GitBranch, History, GitCompare, Upload, Download, Clock, 
  User, RefreshCw, ChevronRight, Check, AlertCircle, FileText
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { useCurrency } from '../utils/currencyUtils';

const ScenarioVersioningPage = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, currencySymbol } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [scenario, setScenario] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareVersions, setCompareVersions] = useState({ a: null, b: null });
  const [compareResult, setCompareResult] = useState(null);
  const [newVersionNotes, setNewVersionNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [versionsRes, scenarioRes] = await Promise.all([
        api.get(`/scenarios/versions/${scenarioId}`),
        api.get(`/scenarios/${scenarioId}`)
      ]);
      setVersions(versionsRes.data.versions || []);
      setScenario(scenarioRes.data);
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => {
    if (scenarioId) {
      fetchData();
    }
  }, [scenarioId, fetchData]);

  const handleCreateVersion = async () => {
    try {
      setCreating(true);
      await api.post('/scenarios/versions', {
        scenario_id: scenarioId,
        notes: newVersionNotes,
        auto_increment: true
      });
      toast.success('Version created successfully');
      setShowCreateModal(false);
      setNewVersionNotes('');
      fetchData();
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Failed to create version');
    } finally {
      setCreating(false);
    }
  };

  const handleCompare = async () => {
    if (!compareVersions.a || !compareVersions.b) {
      toast.error('Please select two versions to compare');
      return;
    }
    
    try {
      setComparing(true);
      const response = await api.post('/scenarios/versions/compare', {
        version_a_id: compareVersions.a,
        version_b_id: compareVersions.b
      });
      setCompareResult(response.data);
    } catch (error) {
      console.error('Error comparing versions:', error);
      toast.error('Failed to compare versions');
    } finally {
      setComparing(false);
    }
  };

  const handlePromote = async () => {
    if (!selectedVersion) return;
    
    try {
      setPromoting(true);
      await api.post('/scenarios/versions/promote', {
        scenario_id: scenarioId,
        version_id: selectedVersion.version_id
      });
      toast.success(`Version ${selectedVersion.name} promoted to current`);
      setShowPromoteModal(false);
      setSelectedVersion(null);
      fetchData();
    } catch (error) {
      console.error('Error promoting version:', error);
      toast.error('Failed to promote version');
    } finally {
      setPromoting(false);
    }
  };

  // formatCurrency now comes from useCurrency hook

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-2">
            <GitBranch className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Scenario Versioning
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            {scenario?.name || 'Scenario'} — {versions.length} version{versions.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCompareModal(true)} disabled={versions.length < 2}>
            <GitCompare className="w-4 h-4 mr-2" />
            Compare Versions
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Create Version
          </Button>
        </div>
      </div>

      {/* Version List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Version History
          </CardTitle>
          <CardDescription>All saved snapshots of this scenario</CardDescription>
        </CardHeader>
        <CardContent>
          {versions.length === 0 ? (
            <div className="text-center py-12 text-[var(--clr-text-secondary)]">
              <GitBranch className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No versions yet. Create your first version to start tracking changes.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version, index) => (
                <div
                  key={version.version_id}
                  className={`flex items-center gap-4 p-4 border rounded-lg hover:bg-[var(--clr-surface-subtle)] transition-colors ${
                    version.is_current ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)]/5' : ''
                  }`}
                  data-testid={`version-${version.version_number}`}
                >
                  {/* Version indicator */}
                  <div className="relative">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      version.is_current 
                        ? 'bg-[var(--clr-brand-primary)] text-white' 
                        : 'bg-[var(--clr-surface-subtle)]'
                    }`}>
                      <span className="font-bold">{version.name}</span>
                    </div>
                    {index < versions.length - 1 && (
                      <div className="absolute top-full left-1/2 w-px h-4 bg-gray-300 transform -translate-x-1/2" />
                    )}
                  </div>

                  {/* Version info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[var(--clr-text-primary)]">
                        Version {version.version_number}
                      </span>
                      {version.is_current && (
                        <Badge className="bg-green-100 text-green-800">Current</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-[var(--clr-text-secondary)]">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {version.author || version.author_email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {version.created_at ? new Date(version.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    {version.notes && (
                      <p className="text-sm text-[var(--clr-text-secondary)] mt-1 line-clamp-1">
                        {version.notes}
                      </p>
                    )}
                  </div>

                  {/* Totals */}
                  <div className="text-right hidden md:block">
                    <p className="text-sm text-[var(--clr-text-secondary)]">Allocated</p>
                    <p className="font-medium">{formatCurrency(version.totals?.total_allocated)}</p>
                  </div>

                  <div className="text-right hidden md:block">
                    <p className="text-sm text-[var(--clr-text-secondary)]">Line Items</p>
                    <p className="font-medium">{version.line_items_count}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedVersion(version);
                        setShowPromoteModal(true);
                      }}
                      disabled={version.is_current}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCompareVersions(prev => ({ ...prev, a: version.version_id }));
                        setShowCompareModal(true);
                      }}
                    >
                      <GitCompare className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Version Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Save a snapshot of the current scenario state
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Notes (optional)</label>
              <Textarea
                placeholder="What changes are in this version?"
                value={newVersionNotes}
                onChange={(e) => setNewVersionNotes(e.target.value)}
                data-testid="version-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateVersion} disabled={creating}>
              {creating && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Create Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Compare Versions</DialogTitle>
            <DialogDescription>
              Select two versions to see differences
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Version A (Baseline)</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={compareVersions.a || ''}
                  onChange={(e) => setCompareVersions(prev => ({ ...prev, a: e.target.value }))}
                  data-testid="compare-version-a"
                >
                  <option value="">Select version</option>
                  {versions.map((v) => (
                    <option key={v.version_id} value={v.version_id}>
                      {v.name} - {new Date(v.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Version B (Compare)</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={compareVersions.b || ''}
                  onChange={(e) => setCompareVersions(prev => ({ ...prev, b: e.target.value }))}
                  data-testid="compare-version-b"
                >
                  <option value="">Select version</option>
                  {versions.map((v) => (
                    <option key={v.version_id} value={v.version_id}>
                      {v.name} - {new Date(v.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Button onClick={handleCompare} disabled={comparing || !compareVersions.a || !compareVersions.b}>
              {comparing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Compare
            </Button>

            {/* Comparison Results */}
            {compareResult && (
              <div className="mt-4 space-y-4">
                <div className="p-4 bg-[var(--clr-surface-subtle)] rounded-lg">
                  <h4 className="font-medium mb-2">Summary</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-600">
                        {compareResult.diff?.line_items_diff?.added || 0}
                      </p>
                      <p className="text-xs text-[var(--clr-text-secondary)]">Added</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">
                        {compareResult.diff?.line_items_diff?.removed || 0}
                      </p>
                      <p className="text-xs text-[var(--clr-text-secondary)]">Removed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">
                        {compareResult.diff?.line_items_diff?.changed || 0}
                      </p>
                      <p className="text-xs text-[var(--clr-text-secondary)]">Changed</p>
                    </div>
                  </div>
                </div>

                {compareResult.diff?.summary?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Changes</h4>
                    <ul className="space-y-1">
                      {compareResult.diff.summary.map((item, idx) => (
                        <li key={idx} className="text-sm flex items-center gap-2">
                          <ChevronRight className="w-4 h-4 text-[var(--clr-text-secondary)]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Promote Modal */}
      <Dialog open={showPromoteModal} onOpenChange={setShowPromoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promote Version</DialogTitle>
            <DialogDescription>
              Restore scenario to this version&apos;s state
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">This action will:</p>
                <ul className="text-sm text-amber-700 mt-1 space-y-1">
                  <li>• Create a backup of the current state</li>
                  <li>• Restore line items from {selectedVersion?.name}</li>
                  <li>• Mark {selectedVersion?.name} as the current version</li>
                </ul>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handlePromote} disabled={promoting}>
              {promoting && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Promote Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScenarioVersioningPage;
